import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgOtpInputComponent } from 'ng-otp-input';
import { Subscription, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { PerfilService } from 'src/app/perfil/perfil.service';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { PasswordRecoveryService, SecretData } from 'src/app/shared/services/password-recovery.service';
import { ValidacaoCadastroService } from 'src/app/shared/services/validacao-cadastro.service';
import { ToastService } from 'src/app/shared/toast';

export enum VerificationMethodTypeEnum {
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
}

@Component({
  selector: 'app-token-confirmation',
  templateUrl: './token-confirmation.component.html',
  styleUrls: ['./token-confirmation.component.scss']
})
export class TokenConfirmationComponent implements OnInit, OnDestroy {

  @Input()
  verificationMethodType: VerificationMethodTypeEnum | 'EMAIL' | 'PHONE';

  @Input()
  initialValue: string;

  @Input()
  isInternal = false;

  @Input()
  pathToRedirectOnError = 'recuperar-senha/confirmar-dados';

  @Input()
  enableVerificationAccount = true;

  @Output()
  onTokenConfirm: EventEmitter<SecretData> = new EventEmitter<SecretData>();

  token: string;
  readonly timeToRetry = 30; // Segundos;
  resendTokenCount = 0;
  secondTicks = 30;
  disableResendToken = false;
  timerInProgress = false;
  loading = false;
  tentativas = 3;
  phonePatternRegex = /^\(?(?:[14689][1-9]|2[12478]|3[1234578]|5[1345]|7[134579])\)? ?(?:[2-8]|9[1-9])[0-9]{3}\-?[0-9]{4}$/;
  isTokenSended = false;
  sendTokenLoader = false;

  sendTokenForm: FormGroup;
  sendTokenResponseError: string | null = null;

  errorAttempts = 0;

  @ViewChild(NgOtpInputComponent, { static: false }) ngOtpInput: NgOtpInputComponent;

  checkoutForm = this.formBuilder.group({
    token: '',
    address: ''
  });

  private readonly HTTP_BUSSINESS_ERROR = 422;
  alteracaoId: string;
  updatePhoneId: string;
  isUpdate = false;
  timerSubscription: Subscription | null = null;
  MILLISECONDS_IN_SECOND = 1000;

  constructor(
    private toastService: ToastService,
    private loaderService: LoaderService,
    public validacaoCadastroService: ValidacaoCadastroService,
    private perfilService: PerfilService,
    private formBuilder: FormBuilder,
    private router: Router,
    private passwordRecoveryService: PasswordRecoveryService,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) { }

  ngOnInit(): void {
    this.buscarParametroTentativas();

    const validators = [Validators.pattern(this.phonePatternRegex), Validators.minLength(11)];
    this.sendTokenForm = new FormGroup({
      phone: new FormControl({ value: '', disabled: true }, validators),
      email: new FormControl({ value: '', disabled: true }, [Validators.required])
    });
    this.fieldByType.setValue(this.initialValue);

    this.sendToken();
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  onOtpChange(e: any) {
    this.token = e;
    if (this.sendTokenResponseError) {
      this.sendTokenResponseError = null;
    }
  }

  async sendToken() {
    try {
      this.sendTokenLoader = true;
      await this.passwordRecoveryService.sendPasswordRecoveryToken(this.verificationMethodType, this.isInternal);
      this.isTokenSended = true;

      if (this.resendTokenCount === this.tentativas) {
        this.closeByErrorAttempts(this.verificationMethodType);
        return;
      }

      this.registerTimerSubscription(true);
    } catch (errResponse) {
      this.toastService.show({ title: 'Não foi possível enviar o código.', text: `Tente novamente.`, type: 'error' });
    } finally {
      this.sendTokenLoader = false;
    }
  }

  async handleConfirmTokenByType(e: Event) {
    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'verificar'
    });
    if (e) { e.preventDefault(); }
    if (this.errorAttempts === this.tentativas) {
      this.closeByErrorAttempts(this.verificationMethodType);
      return;
    }

    this.confirmToken();
  }

  async confirmTokenEmail(): Promise<void> {
    try {
      this.loading = true;
      const confirmarTokenRequest = {
        id: this.alteracaoId,
        token: this.token,
      };
      await this.perfilService.confirmarTokenEmail(confirmarTokenRequest);
      this.confirmTokenSuccessHandler();
    } catch (responseError) {
      this.confirmTokenErrorResponseHandler(responseError);
      this.errorAttempts = this.errorAttempts + 1;
    } finally {
      this.loading = false;
    }
  }

  private confirmTokenSuccessHandler() {
    this.toastService.show({ title: 'Confirmado!', text: `Validação de segurança feita com sucesso.`, type: 'success' });
  }

  async confirmToken() {
    try {
      this.loading = true;
      const res = await this.passwordRecoveryService.validatePasswordRecoveryToken(this.token, this.isInternal);
      this.toastService.show({ title: 'Confirmado!', text: `Validação de segurança feita com sucesso.`, type: 'success' });
      this.onTokenConfirm.emit({ secret: res.secret, token: this.token });
    } catch (responseError) {
      this.confirmTokenErrorResponseHandler(responseError);
      this.errorAttempts = this.errorAttempts + 1;
    } finally {
      this.loading = false;
    }
  }

  private confirmTokenErrorResponseHandler(responseError: any) {
    if (!responseError && !responseError.error) {
      this.toastService.show({ title: 'Não foi possível confirmar o código.', text: `Tente novamente.`, type: 'error' });
    }

    this.googleAnalyticsService.pushActionEvent({
      event: 'view_error',
      detail: responseError?.error?.message || 'Erro ao confirmar token'
    });

    if (responseError.status === this.HTTP_BUSSINESS_ERROR) {
      const isTokenExpired = (responseError.error.message as string).includes('expirado');
      this.sendTokenResponseError = isTokenExpired ? 'Código expirado. Por favor reenvie o código.' : 'Código incorreto.';
      this.stopResendTokenTimer();
    } else {
      this.toastService.show({ title: 'Não foi possível confirmar o código.', text: `Tente novamente.`, type: 'error' });
    }
  }

  private closeByErrorAttempts(type: VerificationMethodTypeEnum | 'EMAIL' | 'PHONE') {
    const toastConfig = {
      [VerificationMethodTypeEnum.EMAIL]: {
        title: 'Não foi possível confirmar seu e-mail.',
        text: `Responda as perguntas de segurança para recuperar sua senha.`
      },
      [VerificationMethodTypeEnum.PHONE]: {
        title: 'Não foi possível confirmar seu celular.',
        text: `Responda as perguntas de segurança para recuperar sua senha.`,
      }
    };

    if (this.isInternal && !this.enableVerificationAccount) {
      toastConfig.PHONE.text = 'Atualize o seu número e esteja com o aparelho em mãos.';
      toastConfig.EMAIL.text = 'Revise se o seu e-mail está atualizado no cadastro.';
    }

    if (this.isInternal && this.enableVerificationAccount) {
      toastConfig.PHONE.title = 'Não foi possível completar a verificação.'
      toastConfig.PHONE.text = 'Responda as perguntas de segurança para alterar o seu perfil.';
      toastConfig.EMAIL.title = 'Não foi possível completar a verificação.';
      toastConfig.EMAIL.text = 'Responda as perguntas de segurança para alterar o seu perfil.';
    }

    this.toastService.show({ ...toastConfig[type], type: 'error' });
    this.googleAnalyticsService.pushActionEvent({
      event: 'view_error',
      detail: toastConfig[type].title
    });

    return this.router.navigate([this.pathToRedirectOnError]);
  }

  private stopResendTokenTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.timerInProgress = false;
  }

  registerTimerSubscription(isFirstCall = false) {
    if (this.timerInProgress) {
      return;
    }

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.disableResendToken = true;

    this.timerSubscription = timer(0, this.MILLISECONDS_IN_SECOND).pipe(take(this.timeToRetry + 1)).subscribe(
      (c) => this.updateTimer(c),
      (err) => console.log(err),
      () => this.completeTimer(isFirstCall),
    );
  }

  private completeTimer(isFirstCall: boolean) {
    this.secondTicks = 30;
    if (!isFirstCall) {
      this.resendTokenCount++;
    }

    this.timerInProgress = false;
    this.timerSubscription.unsubscribe();
    this.timerSubscription = null;
  }

  private updateTimer(count: number) {
    this.timerInProgress = true;
    this.secondTicks = this.timeToRetry - count;
  }

  async handleResendToken(event: Event) {
    try {
      if (event) {
        event.preventDefault();
      }

      if (this.resendTokenCount === this.tentativas) {
        return this.closeByErrorAttempts(this.verificationMethodType);
      }

      this.token = '';
      this.ngOtpInput.setValue('');
      this.registerTimerSubscription();
      await this.sendToken();
    } catch (response) {
      this.toastService.show({ title: 'Não foi possível reenviar o código.', text: `Tente novamente.`, type: 'error' });
    } finally {
      this.loaderService.stop();
    }
  }

  async buscarParametroTentativas() {
    try {
      if (this.isInternal) {
        const response = await this.validacaoCadastroService.buscarParametroTentativas();
        this.tentativas = parseInt(response.valor, 10) ? parseInt(response.valor, 10) : 3;
      }

      this.tentativas = 3;
    } catch (error) {
      this.tentativas = 3;
    }
  }

  get habilitarBotaoEnviar() {
    return this.token?.length === 6;
  }

  allDigitsAreEquals(control: FormControl) {
    const valueToValidate = control.value as string;
    if (valueToValidate) {
      const splitedValue = valueToValidate.split('');
      const allDigitsAreEquals = splitedValue.every((digit: string) => digit === valueToValidate[0]);
      if (allDigitsAreEquals) {
        return {
          allDigitsAreEquals: {
            message: 'Informe um número de celular válido'
          }
        };
      }
    }
  }

  hasAnyError(fieldName: string): boolean {
    return this.sendTokenForm.get(fieldName).touched && !!this.sendTokenForm.get(fieldName).errors;
  }

  get showCancelButton() {
    const phoneControl = this.sendTokenForm.get('phone');
    return phoneControl.value?.length > 0 && (phoneControl.value.length < 11 || this.hasAnyError('phone'));
  }

  get showValidatedPhoneButton() {
    const phoneControl = this.sendTokenForm.get('phone');
    return !phoneControl.errors && phoneControl.value?.length === 11;
  }

  get phoneValue() {
    return this.sendTokenForm.get('phone').value;
  }

  set phoneValue(value: string) {
    this.phoneValue = value;
  }

  get disableSendTokenButton() {
    if (this.verificationMethodType === VerificationMethodTypeEnum.PHONE) {
      return this.disablePhoneSendBtn();
    } else {
      return this.disableEmailSendBtn();
    }
  }

  private disablePhoneSendBtn() {
    const phoneControl = this.sendTokenForm.get('phone');
    return !!phoneControl.errors || this.timerInProgress || phoneControl?.value < 11;
  }

  private disableEmailSendBtn() {
    const emailControl = this.sendTokenForm.get('email');
    return !!emailControl.errors || this.timerInProgress;
  }

  redirectPerfil() {
    this.router.navigate(['portal/perfil']);
  }

  get showTokenResponseError() {
    return this.habilitarBotaoEnviar && this.sendTokenResponseError;
  }

  get fieldByType() {
    if (this.verificationMethodType === VerificationMethodTypeEnum.EMAIL) {
      return this.sendTokenForm.get('email');
    }

    return this.sendTokenForm.get('phone');
  }

  get isRegistration() {
    return true;
  }

}